// Compliance review-date helpers. A "review date" is the NEXT due date for a
// recurring inspection / certificate (WAH, FRA, EICR, gas, LOLER, legionella…).
// dueStatus() classifies one date; complianceSummary()/complianceChip() roll a
// whole section up for the property record and the property cards.

import { TONES } from '../shared/StatusChip';
import { todayLocalISO } from './dates';
import { plural } from './format';

const DAY = 24 * 60 * 60 * 1000;
const isoDays = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY : NaN;
};

/** Classify a due date → { level, label, tone, bg, fg, days }.
 *  level: 'overdue' | 'soon' | 'ok' | 'none'. `soonDays` window defaults to 30.
 *  Day maths is done on calendar dates in Europe/London, so "due today" is right
 *  either side of midnight in BST. The label always carries the meaning in words;
 *  `tone` feeds <StatusChip tone=…> / <Chip tone=…>. */
export function dueStatus(dateStr, { soonDays = 30 } = {}) {
  const none = { level: 'none', label: 'Not set', tone: 'muted', bg: TONES.muted.bg, fg: TONES.muted.fg, days: null };
  if (!dateStr) return none;
  const target = isoDays(String(dateStr).slice(0, 10));
  if (Number.isNaN(target)) return none;
  const days = Math.round(target - isoDays(todayLocalISO()));
  if (days < 0) return { level: 'overdue', label: `Overdue by ${plural(Math.abs(days), 'day')}`, tone: 'danger', bg: TONES.danger.bg, fg: TONES.danger.fg, days };
  if (days <= soonDays) return { level: 'soon', label: days === 0 ? 'Due today' : `Due soon · ${plural(days, 'day')}`, tone: 'warning', bg: TONES.warning.bg, fg: TONES.warning.fg, days };
  return { level: 'ok', label: 'In date', tone: 'success', bg: TONES.success.bg, fg: TONES.success.fg, days };
}

/** Roll up all date fields in a section → { overdue, soon, ok, set, total, next }.
 *  `next` = the most urgent date (soonest upcoming, or most overdue) with its label. */
export function complianceSummary(values = {}, spec = []) {
  const dateFields = (spec || []).filter((f) => f.type === 'date');
  let overdue = 0, soon = 0, ok = 0, set = 0, next = null;
  for (const f of dateFields) {
    const v = values?.[f.key];
    if (!v) continue;
    set++;
    const s = dueStatus(v);
    if (s.level === 'overdue') overdue++;
    else if (s.level === 'soon') soon++;
    else if (s.level === 'ok') ok++;
    if (s.days != null && (next == null || s.days < next.days)) next = { label: f.label, date: v, ...s };
  }
  return { overdue, soon, ok, set, total: dateFields.length, next };
}

/** A single chip descriptor for a property card, or null when nothing is set. */
export function complianceChip(values, spec) {
  const s = complianceSummary(values, spec);
  if (!s.set) return null;
  if (s.overdue) return { label: `${s.overdue} overdue`, tone: 'danger', bg: TONES.danger.bg, fg: TONES.danger.fg, icon: 'fa-triangle-exclamation' };
  if (s.soon) return { label: `${s.soon} due soon`, tone: 'warning', bg: TONES.warning.bg, fg: TONES.warning.fg, icon: 'fa-clock' };
  return { label: 'All in date', tone: 'success', bg: TONES.success.bg, fg: TONES.success.fg, icon: 'fa-circle-check' };
}

// Shared spec for the property compliance section — used on the property record
// AND (imported) to compute the card chip so both stay in sync.
export const COMPLIANCE_SPEC = [
  { key: 'wahReview', label: 'Working-at-Height (WAH) review', type: 'date' },
  { key: 'riskInspection', label: 'Risk inspection (RI)', type: 'date' },
  { key: 'rreReview', label: 'Rescue & Emergency Plan (RRE)', type: 'date' },
  { key: 'wcas', label: 'Water compliance (WCAS)', type: 'date' },
  { key: 'fireRiskAssessment', label: 'Fire Risk Assessment (FRA)', type: 'date' },
  { key: 'fireDoorInspection', label: 'Fire door inspection', type: 'date' },
  { key: 'eicr', label: 'Electrical installation (EICR)', type: 'date' },
  { key: 'emergencyLighting', label: 'Emergency lighting test', type: 'date' },
  { key: 'gasSafety', label: 'Gas safety (CP12 / GAS)', type: 'date' },
  { key: 'legionella', label: 'Legionella / water risk', type: 'date' },
  { key: 'asbestosReinspection', label: 'Asbestos re-inspection', type: 'date' },
  { key: 'lolerThoroughExam', label: 'LOLER thorough examination', type: 'date' },
  { key: 'lightningProtection', label: 'Lightning protection test', type: 'date' },
  { key: 'flagpoleInspection', label: 'Flagpole inspection', type: 'date' },
  { key: 'staircaseInspection', label: 'Staircase inspection', type: 'date' },
  { key: 'equipmentLogbook', label: 'Equipment logbook update', type: 'date' },
  { key: 'fireAlarmService', label: 'Fire alarm service (BS 5839-1)', type: 'date' },
  { key: 'fireExtinguisherService', label: 'Fire extinguisher service', type: 'date' },
  { key: 'sprinklerInspection', label: 'Sprinkler inspection', type: 'date' },
  { key: 'aovSmokeVentTest', label: 'AOV / smoke vent test', type: 'date' },
  { key: 'patTesting', label: 'Portable appliance testing (PAT)', type: 'date' },
  { key: 'waterTempMonitoring', label: 'Water temperature monitoring (L8)', type: 'date' },
  { key: 'roofSafetyInspection', label: 'Roof safety system inspection', type: 'date' },
  { key: 'ppm', label: 'Next PPM visit', type: 'date' },
];
